import {dateJJMM} from './atelierRules';
import {eurosFr, nombreFr} from './recapRules';
import {appliquerCalendrier, valeursDepuisSimulation} from './simulationRules';

/**
 * TOUR-03 lot 7 (D-1 à D-5) — règles pures de l'erreur mesurée (R-TOUR-03-9) et de l'analyse de sensibilité.
 * AUCUN recalcul : coûts, écarts, journées, étalons et médianes viennent de l'API, on ne fait que les mettre en forme.
 */

export const AIDE_ERREUR = "écart coût simulé / coût réel sur les campagnes passées de cette typologie ; le prix se calcule hors outil (R-TOUR-03-10)";
export const POLLING_SENSIBILITE_MS = 5000;
export const COULEUR_BAISSE = '#87BC26';
export const COULEUR_HAUSSE = '#F39A3F';
export const COULEUR_INCONNU = '#8A929C';

const VARIANTES = {calibre: 'curseurs calibrés', defaut: 'curseurs par défaut'};

const pluriel = (n, un, plusieurs) => `${n} ${n >= 2 ? plusieurs : un}`;

/** « −8 % » / « +12,5 % » depuis une fraction de l'API (0,08 → « +8 % »). */
export const pctSigne = (fraction) => {
    if (fraction == null) return '—';
    const v = Math.round(Number(fraction) * 1000) / 10;
    return `${v > 0 ? '+' : v < 0 ? '−' : ''}${nombreFr(Math.abs(v))} %`;
};

/** Clé d'ouverture de l'atelier : le volet n'est pré-rempli qu'une fois par couple scénario source / cran (pas à chaque rafraîchissement du cache). */
export const cleOuverture = (fromId, cran) => `${fromId ?? ''}|${cran ?? ''}`;
export const doitPreRemplir = (dejaInitialise, fromId, cran) => dejaInitialise !== cleOuverture(fromId, cran);

/** D-1 : « Erreur mesurée : ±8 % (3 épisodes, rural) » ; sans étalon : « Erreur mesurée : aucun étalon ». */
export function pastilleErreur(erreurMesuree, typologie = null) {
    const {valeur, source, n} = erreurMesuree || {};
    typologie = erreurMesuree?.typologie ?? typologie; // l'API l'expose sur erreurMesuree quand elle est à jour ; sinon repli sur le read model erreur
    if (valeur == null) return {etat: 'aucun', libelle: 'Erreur mesurée : aucun étalon'};
    const contexte = source === 'patrimoine' ? 'ce patrimoine' : (typologie || 'typologie');
    return {
        etat: 'mesuree',
        libelle: `Erreur mesurée : ±${nombreFr(Math.round(Math.abs(Number(valeur)) * 100))} % (${[VARIANTES[erreurMesuree?.variante], pluriel(Number(n ?? 0), 'épisode', 'épisodes'), contexte].filter(Boolean).join(', ')})`,
    };
}

/** Version courte pour le bandeau de l'atelier : « ±138 % », libellé complet et aide en infobulle ; sans valeur mesurée, pas de pilule. */
export function pastilleErreurCourte(erreurMesuree, typologie = null) {
    const p = pastilleErreur(erreurMesuree, typologie);
    if (p.etat !== 'mesuree') return null;
    return {libelle: `±${nombreFr(Math.round(Math.abs(Number(erreurMesuree.valeur)) * 100))} %`, infobulle: `${p.libelle} — ${AIDE_ERREUR}`, etat: p.etat};
}

/* ------------------------------------------------------------------ D-5 : étalons */

/** Une ligne par épisode, valeurs recopiées de l'API ; un épisode `incomplet` est grisé (hors médianes côté API). */
export function lignesEtalons(erreur) {
    return (erreur?.episodes ?? []).map((e) => ({
        cle: `${e.du}|${e.au}`,
        periode: `${dateJJMM(e.du)} → ${dateJJMM(e.au)}`,
        journees: `${e.journeesReelles ?? '—'} / ${e.journeesSimulees ?? '—'}`,
        bacs: e.bacsReels == null ? '—' : String(e.bacsReels),
        coutReel: eurosFr(e.coutReel),
        coutSimule: eurosFr(e.coutSimule),
        erreur: pctSigne(e.erreur),
        erreurCalibre: pctSigne(e.erreurCalibre),
        incomplet: e.incomplet === true,
        couverture: e.incomplet === true && e.couverture != null ? `couverture ${Math.round(Number(e.couverture) * 100)} %` : null,
    }));
}

/** Médianes du patrimoine et de la typologie, telles que l'API les donne ; une médiane absente disparaît. */
export function medianesEtalons(erreur) {
    if (!erreur) return [];
    return [
        {cle: 'patrimoine', libelle: 'Médiane du patrimoine', stats: erreur.patrimoine},
        {cle: 'typologie', libelle: `Médiane ${erreur.typologie ?? 'de la typologie'}`, stats: erreur.typologieStats},
    ].filter((m) => m.stats?.erreurMediane != null).map((m) => ({
        cle: m.cle,
        libelle: m.libelle,
        n: m.stats.n,
        erreur: pctSigne(m.stats.erreurMediane),
        erreurCalibre: pctSigne(m.stats.calibre?.erreurMediane),
    }));
}

/* ------------------------------------------------------------------ D-2 : tornado */

/** Lignes triées par |deltaPct| décroissant (ordre stable), sens, couleur et largeur (% de la plus grande barre). */
export function lignesTornado(sensibilite) {
    const lignes = (sensibilite?.lignes ?? []).map((l, i) => ({l, i}))
        .sort((a, b) => Math.abs(b.l.deltaPct ?? 0) - Math.abs(a.l.deltaPct ?? 0) || a.i - b.i);
    const max = Math.max(0, ...lignes.map(({l}) => Math.abs(l.deltaPct ?? 0)));
    return lignes.map(({l}) => {
        const inconnu = l.deltaPct == null;
        const delta = l.deltaPct ?? 0;
        const sens = inconnu ? 'inconnu' : delta < 0 ? 'baisse' : delta > 0 ? 'hausse' : 'neutre';
        return {
            cle: `${l.curseur}|${l.cran}`,
            libelle: l.libelle,
            sens,
            couleur: sens === 'inconnu' ? COULEUR_INCONNU : sens === 'baisse' ? COULEUR_BAISSE : COULEUR_HAUSSE,
            largeur: max > 0 ? Math.round((Math.abs(delta) / max) * 1000) / 10 : 0,
            valeur: `${inconnu ? '—' : `${delta > 0 ? '+' : delta < 0 ? '−' : ''}${nombreFr(Math.abs(delta))} %`} · ${eurosFr(l.coutTotal)} · ${pluriel(Number(l.journees ?? 0), 'journée', 'journées')}`,
            contrainte: l.degrade === true,
            parametres: l.parametres,
        };
    });
}

/** Ligne de référence en tête : « Scénario de référence · 9 923 € · 20 journées ». */
export function referenceTornado(sensibilite) {
    const r = sensibilite?.reference;
    if (!r) return null;
    return `${r.libelle ?? 'Scénario de référence'} · ${eurosFr(r.coutTotal)} · ${pluriel(Number(r.journees ?? 0), 'journée', 'journées')}`;
}

/* ------------------------------------------------------------------ D-3 : appliquer un cran */

/**
 * Remplit le volet avec les `parametres` de la ligne (fournis par l'API), sans rien recalculer et sans jouer.
 * Le libellé et la période du volet sont conservés ; le résultat est un brouillon que l'exploitant joue lui-même.
 */
export function appliquerCran(valeurs, ligne) {
    if (!ligne?.parametres) return valeurs;
    return {
        ...valeursDepuisSimulation({du: valeurs.du, au: valeurs.au, parametres: ligne.parametres}),
        libelle: valeurs.libelle,
    };
}

/** `?cran=heuresParJour~1` ↔ ligne de l'API (la clé d'une ligne est `curseur|cran`). */
export const cleCranUrl = (cle) => String(cle ?? '').replace('|', '~');
export const ligneDeCran = (sensibilite, param) =>
    (sensibilite?.lignes ?? []).find((l) => cleCranUrl(`${l.curseur}|${l.cran}`) === param) ?? null;

/** Message d'une lecture en erreur (corps API, sinon texte générique). */
export const messageErreurLecture = (e) => e?.data?.detail || e?.data?.['hydra:description'] || e?.data?.message || "L'analyse de sensibilité n'a pas pu être lue.";

/**
 * Applique un cran au volet par `setValues` UNIQUEMENT : aucun appel réseau, rien n'est joué. Le calendrier absent force `contraint` à faux,
 * comme à l'ouverture de l'atelier.
 */
export function appliquerCranAuVolet(setValues, valeurs, ligne, aCalendrier) {
    setValues(appliquerCalendrier(appliquerCran(valeurs, ligne), aCalendrier));
}

/* ------------------------------------------------------------------ D-4 : statut et polling */

/** 5 s tant que l'analyse tourne (ou vient d'être lancée et pas encore lue) ; 0 dès `done` / `failed`. */
export function pollingSensibilite(sensibilite, lancee = false, enErreur = false) {
    if (enErreur) return 0;
    if (!sensibilite) return lancee ? POLLING_SENSIBILITE_MS : 0;
    return sensibilite.statut === 'en_cours' ? POLLING_SENSIBILITE_MS : 0;
}

/** Décision de Jérôme : le lancement de l'analyse est désactivé côté front (calcul trop long). Passer à true pour le réactiver. */
export const ANALYSE_SENSIBILITE_ACTIVE = false;
export const MESSAGE_ANALYSE_DESACTIVEE = 'Analyse momentanément désactivée : son calcul est trop long.';

/** Rendu selon le statut : bouton, pilule « analyse en cours… », tornado, ou échec (message de l'API + relance). */
export function etatSensibilite(sensibilite, {lancee = false, erreurLecture = null} = {}) {
    if (erreurLecture && !sensibilite) return {etat: 'failed', bouton: "Relancer l'analyse", pilule: null, message: erreurLecture};
    if (sensibilite?.statut === 'done') return {etat: 'done', bouton: 'Relancer l\'analyse', pilule: null, message: null};
    if (sensibilite?.statut === 'failed') {
        return {etat: 'failed', bouton: 'Relancer l\'analyse', pilule: null, message: sensibilite.erreur || "L'analyse de sensibilité a échoué."};
    }
    if (sensibilite?.statut === 'en_cours' || lancee) return {etat: 'en_cours', bouton: null, pilule: 'analyse en cours…', message: null};
    return {etat: 'aucune', bouton: 'Analyser la sensibilité', pilule: null, message: null};
}
